import { DialogController, isPopupForced } from './dialog-controller';
import { ActiveInvestSession } from './invest-session';
import { navigation } from './navigation';
import { preconnect } from './preconnect';
import { buildThemeQuery } from './theme-params';
import type {
  CestoConfig,
  InvestOptions,
  InvestSession,
  InvestTheme,
} from './types';

export const DEFAULT_BASE_URL = 'https://app.cesto.co';
const POPUP_NAME_PREFIX = 'cesto-invest';
const POPUP_WIDTH = 480;
const POPUP_HEIGHT = 720;

/**
 * Base origins the loader is allowed to talk to. Hosting is fixed to
 * app.cesto.co (plan §1), so the pk, the wallet relay, and the iframe/popup
 * src must only ever point at a cesto.co origin — or localhost for local
 * partner testing. Rejecting anything else stops a CMS-injected
 * `data-cesto-base-url` (or a mistyped config) from rerouting the flow, and the
 * user's wallet prompts, to an attacker-controlled origin.
 */
function isAllowedBaseOrigin(origin: string): boolean {
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  const { hostname, protocol } = url;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return protocol === 'http:' || protocol === 'https:';
  }
  return (
    protocol === 'https:' &&
    (hostname === 'cesto.co' || hostname.endsWith('.cesto.co'))
  );
}

/** Unguessable suffix for the popup window name so another script on the
 * partner page can't `window.open(evil, 'cesto-invest')` to hijack the open
 * invest popup by its (previously fixed) name. */
function randomToken(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Inert session returned when the popup was blocked and we navigated away. */
const INERT_SESSION: InvestSession = {
  focus: () => undefined,
  close: () => undefined,
  isOpen: false,
};

/** What Cesto needs from an open dialog or popup session. */
interface ActiveController {
  readonly session: InvestSession;
  readonly isOpen: boolean;
  focus(): void;
}

function popupFeatures(): string {
  const left = Math.max(
    0,
    Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2),
  );
  const top = Math.max(
    0,
    Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2),
  );
  return `popup,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
}

/**
 * Loader for the Cesto invest dialog/popup. Holds no tokens, makes no
 * requests, stores no state beyond the currently open session. Everything
 * sensitive happens on the Cesto origin inside the dialog iframe or popup.
 */
export class Cesto {
  private readonly apiKey: string;
  private readonly baseUrl: string;
  private readonly baseOrigin: string;
  private readonly popupName: string;
  private readonly defaultTheme: InvestTheme | undefined;
  private active: ActiveController | null = null;

  constructor(config: CestoConfig) {
    if (!config || typeof config.apiKey !== 'string') {
      throw new Error('[Cesto] apiKey is required');
    }
    this.apiKey = config.apiKey;
    this.baseUrl = (config.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, '');
    this.baseOrigin = new URL(this.baseUrl).origin;
    if (!isAllowedBaseOrigin(this.baseOrigin)) {
      throw new Error(
        `[Cesto] baseUrl must be a cesto.co origin (or localhost for dev); got ${this.baseOrigin}`,
      );
    }
    // Per-instance random name; see randomToken.
    this.popupName = `${POPUP_NAME_PREFIX}-${randomToken()}`;
    this.defaultTheme = config.theme;
    // Warm DNS + TLS to the app origin now (construction happens before the
    // click in the programmatic/React path) so the first open doesn't pay the
    // handshake on the critical path (DLG-6).
    preconnect(this.baseOrigin);
  }

  /**
   * Opens the invest flow. Defaults to `mode: 'dialog'` (in-page overlay
   * iframe). With `mode: 'popup'`, or after a dialog handshake failure was
   * remembered for this tab, a popup window opens instead; in that case this
   * MUST be called synchronously inside a user gesture (click handler) so
   * popup blockers allow the window. Calling it synchronously is a good idea
   * in dialog mode too, since the popup fallback may kick in.
   *
   * If a dialog or popup from this instance is already open, it is focused
   * and the existing session is returned. If the popup is blocked, the page
   * navigates to the invest URL and an inert session is returned — the SDK
   * cannot observe the outcome of that path.
   */
  invest(options: InvestOptions): InvestSession {
    if (this.active?.isOpen) {
      this.active.focus();
      return this.active.session;
    }

    const resolved = this.withDefaultTheme(options);
    const mode = resolved.mode ?? 'dialog';
    if (mode === 'dialog' && !isPopupForced()) {
      return this.openDialog(resolved);
    }
    return this.openPopup(resolved);
  }

  /** Merges the constructor-level theme under per-call values, field by field. */
  private withDefaultTheme(options: InvestOptions): InvestOptions {
    if (!this.defaultTheme && !options.theme) return options;
    return { ...options, theme: { ...this.defaultTheme, ...options.theme } };
  }

  private openDialog(options: InvestOptions): InvestSession {
    const controller = new DialogController({
      baseUrl: this.baseUrl,
      baseOrigin: this.baseOrigin,
      apiKey: this.apiKey,
      options,
      openPopup: () => {
        this.active = null;
        this.openPopup(options);
      },
    });
    this.active = controller;
    return controller.session;
  }

  private openPopup(options: InvestOptions): InvestSession {
    // Theme params apply here too: popup mode opens the same themed page.
    const themeQuery = buildThemeQuery(options.theme);
    const url =
      `${this.baseUrl}/invest/${encodeURIComponent(options.basket)}` +
      `?pk=${encodeURIComponent(this.apiKey)}${themeQuery}`;

    // Synchronous window.open: no awaits or timers between the user gesture
    // and this call.
    const popup = window.open(url, this.popupName, popupFeatures());
    if (!popup) {
      // Popup blocked: full-page fallback.
      //
      // SEC-E: this deliberately sends NO `return_url`. It used to append the
      // partner's current href, but nothing on app.cesto.co has ever read the
      // param — so it bought no return journey, while leaving a
      // ready-to-misuse open-redirect input on a signing origin for whoever
      // implemented it next.
      //
      // Reinstating a return journey is fine, but it must be built with
      // validation in the SAME change: app.cesto.co has to check the URL
      // against the origins registered for this pk before redirecting, and
      // reject anything else. Do not re-add the param on its own.
      navigation.navigate(url);
      return INERT_SESSION;
    }

    const session = new ActiveInvestSession(
      popup,
      this.baseOrigin,
      this.apiKey,
      options,
    );
    this.active = session;
    return session.session;
  }
}
